import React, { useEffect, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import { setPageTitle, removeInfoMessage } from "src/core/redux";
import { SentinelPageProps } from "../../interfaces";
import { SentinelBasePage } from "../sentinel-base";
import {
  Box,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from "@cloudscape-design/components";
import { getTeamPackageList } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { TeamPackageListResponse } from "src/core/sentinel/types";
import { TeamPackage } from "@amzn/dependencysentinel";
import { useParams } from "react-router-dom";
export interface SentinelTeamDetailPageProps extends SentinelPageProps {
  readOnly?: boolean;
  sentinelController: SentinelController;
}
export const TeamDetailPage: React.FC<SentinelTeamDetailPageProps> = (
  // eslint-disable-next-line
  props: SentinelTeamDetailPageProps
) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [filteringText, setFilteringText] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [teamPackageList, setTeamPackageList] = useState<TeamPackage[]>([]);
  const pageSize = 50;
  const { teamId } = useParams<{ teamId: string }>();
  useEffect(() => {
    const fetchTeamPackages = async () => {
      try {
        const payload = {
          teamName: teamId
        };
        const response = await dispatch(
          getTeamPackageList({
            sentinelController: props.sentinelController,
            teamPackageListInput: payload,
          })
        );
        const teamPackageList = (response as TeamPackageListResponse)?.payload?.payload?.teamPackageList;
        setTeamPackageList(teamPackageList);
        setLoading(false);
      } catch (error) {
        console.error(`Error for getting team pakcages: ${error}`, error);
        setLoading(false);
      }
    };
    fetchTeamPackages();
  }, []);

  const filteredPackages = teamPackageList.filter((p) => {
    const content = [
      p.bindleId,
      p.packageName,
      p.teamName,
    ]
      .join(" ")
      .toLowerCase();

    return (
      !filteringText || content.includes(filteringText.toLowerCase())
    );
  });

  const paginatedItems = filteredPackages.slice(
    (currentPageIndex - 1) * pageSize,
    currentPageIndex * pageSize
  );

  const totalPages = Math.ceil(filteredPackages.length / pageSize);

  useEffect(() => {
    dispatch(setPageTitle("Team Detail"));
    dispatch(removeInfoMessage({ id: 'message_1' }));
  }, []);

  return (
    <SentinelBasePage>
      <Table
        items={paginatedItems}
        columnDefinitions={[
          {
            id: "teamName",
            header: "Team Name",
            cell: (e: TeamPackage) => e.teamName,
            sortingField: "teamName",
            isRowHeader: true,
          },
          {
            id: "packageName",
            header: "Package Name",
            cell: (e: TeamPackage) => (
              <Link href={`#/team/details/${teamId}/${e.packageName}`}>{e.packageName}</Link>
            ),
            sortingField: "packageName",
            isRowHeader: true,
          },
          {
            id: "bindleId",
            header: "Bindle ID",
            cell: (e: TeamPackage) => e.bindleId,
            sortingField: "bindleId",
            isRowHeader: true,
          },
        ]}
        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
          `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        loading={loading}
        loadingText="Loading resources"
        empty={
          <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b>No package found</b>
            </SpaceBetween>
          </Box>
        }
        filter={
          <TextFilter
            filteringPlaceholder="Type any keyword to filter packages"
            filteringText={filteringText}
            onChange={({ detail }) => {
              setFilteringText(detail.filteringText);
              setCurrentPageIndex(1);
            }}
            countText={`${filteredPackages.length} matches`}
          />
        }
        header={<Header>Packages for Team: {teamId}</Header>}
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={totalPages}
            onChange={({ detail }) =>
              setCurrentPageIndex(detail.currentPageIndex)
            }
          />
        }
      />
    </SentinelBasePage>
  );
};
